import React, { useMemo, useState } from "react";
import CommonLayout from "./CommonLayout";
import moment from "moment/moment";
import { orderBy } from "lodash";
import ButtonSecondary from "../../shared/Button/ButtonSecondary";
import { Avatar } from "@mui/material";
import { IUser } from "../../@core/interfaces/IUser";
import { UserTypeEnum } from "../../@core/enums/UserType.enum";
import { useUsers } from "../../hooks/collections/useUsers";
import { useAlert } from "../../@shared/hooks/useAlert";
import Label from "../../components/Label/Label";
import Checkbox from "../../shared/Checkbox/Checkbox";
import { useLoader } from "../../@shared/hooks/useLoader";

const AccountCompanyUsers = () => {
  const alert = useAlert();
  const loader = useLoader();
  const { updateUser, users, updateUserAuthStatus } = useUsers({ loadData: true });
  const [filters, setFilters] = useState({
    showDisabled: false,
    nameFilter: "",
    roleFilter: ""
  });

  const toggleUserStatus = async (user: IUser) => {
    if (!(await alert.confirm("Change user status?", user.name))) return;
    loader.start();
    try {
      await updateUser(user.id, { trash: !user.trash });
      await updateUserAuthStatus(user.id, !!user.trash);
    } catch (error) {
      console.error("Error updating user status:", error);
    } finally {
      loader.stop();
      alert.toastSuccess(`User ${user.trash ? "enabled" : "disabled"} successfully.`);
    }
  };

  const displayData = useMemo(() => {
    return (users || [])
      .filter((user: IUser) => {
        const matchesDisabledFilter = filters.showDisabled ? true : !user.trash;
        const matchesNameFilter = !filters.nameFilter ||
          (user.name && user.name.toLowerCase().includes(filters.nameFilter.toLowerCase())) ||
          (user.email && user.email.toLowerCase().includes(filters.nameFilter.toLowerCase()));
        const matchesRoleFilter = !filters.roleFilter || user.role === filters.roleFilter;

        return matchesDisabledFilter && matchesNameFilter && matchesRoleFilter;
      });
  }, [users, filters]);

  const renderUsersSection = () => {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col md:flex-row gap-5 items-center justify-between">
          <h2 className="text-3xl font-semibold">Users</h2>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        <div className="flex justify-end flex-wrap gap-4 mb-4">
          <div className="flex flex-col">
            <Label className="mr-2">Search:</Label>
            <input
              type="text"
              placeholder="Search by name or email"
              value={filters.nameFilter}
              onChange={(e) => setFilters({ ...filters, nameFilter: e.target.value })}
              className="p-2 border rounded"
            />
          </div>

          <div className="flex flex-col">
            <Label className="mr-2">Role:</Label>
            <select
              value={filters.roleFilter}
              onChange={(e) => setFilters({ ...filters, roleFilter: e.target.value })}
              className="p-2 border rounded pe-8"
            >
              <option value="">All Roles</option>
              <option value={UserTypeEnum.USER}>User</option>
              <option value={UserTypeEnum.PROVIDER}>Provider</option>
            </select>
          </div>

          <div className="flex items-center">
            <Checkbox
              onChange={(checked) => setFilters({ ...filters, showDisabled: checked })}
              name={"showDisabled"}
              label={"Include Disabled Users"}
            />
          </div>
        </div>

        <div className={"overflow-x-auto"}>
          {displayData.length ? (
            <table className={"w-full text-left min-w-[1000px] border-separate border-spacing-4"}>
              <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
              </thead>
              <tbody>
              {orderBy(displayData, ["createdAt"], ["desc"]).map((user: any) => (
                <tr key={user.id} className={user.trash ? "opacity-60" : ""}>
                  <td className={"flex gap-2 items-center"}>{user?.imageUrl &&
                    <Avatar src={user.imageUrl}/>} {user.name} </td>
                  <td>{user.email}</td>
                  <td className={"capitalize"}>{user.role}</td>
                  <td>{moment(user.createdAt).format("YYYY-MM-DD")}</td>
                  <td>{user.trash ? "Disabled" : "Active"}</td>
                  <td>
                    <div className={"flex gap-2"}>
                      <ButtonSecondary onClick={() => toggleUserStatus(user)}>
                        {user.trash ? "Enable Account" : "Disable Account"}
                      </ButtonSecondary>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center h-64 border bg-white/50 dark:border-neutral-6000 dark:bg-neutral-950/50 rounded-lg">
              <p className="text-gray-500 dark:text-white">No users found.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <CommonLayout>{renderUsersSection()}</CommonLayout>
    </div>
  );
};

export default AccountCompanyUsers;
